// src/services/warehouseService.ts
// Warehouse management service

import { fetchApi } from './api';

export interface Warehouse {
  id: number;
  organization: number;
  organization_name: string;
  code: string;
  name: string;
  address: string;
  is_active: boolean;
  is_default: boolean;
  /** CENTRAL/REGIONAL appear in header; BRANCH/STORE are stores (optional for older payloads). */
  warehouse_type?: 'CENTRAL' | 'REGIONAL' | 'BRANCH' | 'STORE';
  /** Lower number = higher priority when allocating scarce stock. */
  replenishment_priority?: number;
  created_at: string;
  updated_at: string;
}

export interface WarehouseStock {
  id: number;
  organization: number;
  organization_name: string;
  warehouse: number;
  warehouse_code: string;
  warehouse_name: string;
  product: number;
  product_code: string;
  product_name: string;
  stock: number;
  reorder_point: number;
  safety_stock: number;
  wh_location: string | null;
  updated_at: string;
  created_at: string;
}

export interface WarehouseStockDetail extends Omit<WarehouseStock, 'warehouse' | 'product'> {
  warehouse: Warehouse;
  product: {
    id: number;
    code: string;
    name: string;
    description: string;
    price: number;
    cost: number;
    // ... otros campos del producto si son necesarios
  };
}

export interface StockSummary {
  total_products: number;
  total_stock_value: number;
  products_low_stock: number;
  products_no_stock: number;
}

export interface ProductStockInfo {
  product: {
    id: number;
    code: string;
    name: string;
    total_stock: number;
  };
  warehouse_stocks: WarehouseStock[];
}

export interface BulkStockUpdate {
  warehouse_id: number;
  updates: Array<{
    product_id: number;
    stock: number;
  }>;
}

export interface BulkStockUpdateResponse {
  updated_items: Array<{
    product_id: number;
    product_code: string;
    old_stock: number;
    new_stock: number;
    total_stock: number;
  }>;
  errors: Array<{
    product_id?: number;
    error: string;
  }>;
  total_updated: number;
  total_errors: number;
}

export class WarehouseService {
  private readonly baseUrl = 'products';

  // Warehouses management
  async getWarehouses(params?: {
    is_active?: boolean;
    is_default?: boolean;
    /** When true, excludes BRANCH/STORE (global header selector). */
    for_header?: boolean;
  }): Promise<Warehouse[]> {
    const response = await fetchApi<{ results: Warehouse[]; count: number; next: string | null; previous: string | null }>(`${this.baseUrl}/warehouses/`, {
      params,
    });
    
    // Return the results array from the paginated response
    return response.results || [];
  }

  async getWarehouse(id: number): Promise<Warehouse> {
    return fetchApi<Warehouse>(`${this.baseUrl}/warehouses/${id}/`);
  }

  async createWarehouse(data: Partial<Warehouse>): Promise<Warehouse> {
    return fetchApi<Warehouse>(`${this.baseUrl}/warehouses/`, {
      method: 'POST',
      body: data,
    });
  }

  async updateWarehouse(id: number, data: Partial<Warehouse>): Promise<Warehouse> {
    return fetchApi<Warehouse>(`${this.baseUrl}/warehouses/${id}/`, {
      method: 'PUT',
      body: data,
    });
  }

  async deleteWarehouse(id: number): Promise<void> {
    return fetchApi<void>(`${this.baseUrl}/warehouses/${id}/`, {
      method: 'DELETE',
    });
  }

  async setWarehouseAsDefault(id: number): Promise<{ status: string }> {
    return fetchApi<{ status: string }>(`${this.baseUrl}/warehouses/${id}/set_as_default/`, {
      method: 'POST',
    });
  }

  async getWarehouseStockSummary(id: number): Promise<StockSummary> {
    return fetchApi<StockSummary>(`${this.baseUrl}/warehouses/${id}/stock_summary/`);
  }

  // Warehouse stocks management
  async getWarehouseStocks(params?: {
    warehouse?: number;
    product?: number;
    product_code?: string;
    low_stock?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<{ results: WarehouseStock[]; count: number; next: string | null; previous: string | null }> {
    return fetchApi<{ results: WarehouseStock[]; count: number; next: string | null; previous: string | null }>(`${this.baseUrl}/warehouse-stocks/`, {
      params,
    });
  }

  async getWarehouseStock(id: number): Promise<WarehouseStockDetail> {
    return fetchApi<WarehouseStockDetail>(`${this.baseUrl}/warehouse-stocks/${id}/`);
  }

  async createWarehouseStock(data: Partial<WarehouseStock>): Promise<WarehouseStock> {
    return fetchApi<WarehouseStock>(`${this.baseUrl}/warehouse-stocks/`, {
      method: 'POST',
      body: data,
    });
  }

  async updateWarehouseStock(id: number, data: Partial<WarehouseStock>): Promise<WarehouseStock> {
    return fetchApi<WarehouseStock>(`${this.baseUrl}/warehouse-stocks/${id}/`, {
      method: 'PUT',
      body: data,
    });
  }

  async deleteWarehouseStock(id: number): Promise<void> {
    return fetchApi<void>(`${this.baseUrl}/warehouse-stocks/${id}/`, {
      method: 'DELETE',
    });
  }

  // Special warehouse stock operations
  async getStocksByWarehouse(
    warehouseId: number,
    params?: {
      product_code?: string;
      low_stock?: boolean;
      page?: number;
      page_size?: number;
    }
  ): Promise<{ results: WarehouseStock[]; count: number; next: string | null; previous: string | null }> {
    return fetchApi<{ results: WarehouseStock[]; count: number; next: string | null; previous: string | null }>(`${this.baseUrl}/warehouse-stocks/by_warehouse/`, {
      params: {
        warehouse_id: warehouseId,
        ...params,
      },
    });
  }

  async getStocksByProduct(productId: number): Promise<ProductStockInfo> {
    return fetchApi<ProductStockInfo>(`${this.baseUrl}/warehouse-stocks/by_product/`, {
      params: {
        product_id: productId,
      },
    });
  }

  async bulkUpdateStock(data: BulkStockUpdate): Promise<BulkStockUpdateResponse> {
    return fetchApi<BulkStockUpdateResponse>(`${this.baseUrl}/warehouse-stocks/bulk_update_stock/`, {
      method: 'POST',
      body: data,
    });
  }

  // Helper method to get default warehouse
  async getDefaultWarehouse(): Promise<Warehouse | null> {
    const warehouses = await this.getWarehouses({ is_default: true, is_active: true });
    return warehouses.length > 0 ? warehouses[0] : null;
  }

  // Helper method to get active warehouses for selector
  async getActiveWarehouses(): Promise<Array<{ value: number; label: string; code: string }>> {
    const warehouses = await this.getWarehouses({ is_active: true });
    return warehouses.map(warehouse => ({
      value: warehouse.id,
      label: `${warehouse.code} - ${warehouse.name}`,
      code: warehouse.code,
    }));
  }
}

// Export singleton instance
export const warehouseService = new WarehouseService();